/**
 * PurchaseReturnDialog — Phase 18
 * --------------------------------------------------------------------------
 * Lets the user return one or more purchased packets to a vendor.
 *
 * Two modes:
 *   • packet  — opened from Packet Detail. Vendor is locked to the packet's
 *               original vendor (cannot be changed by the user).
 *   • vendor  — opened from Vendor Detail. User picks one or more eligible
 *               purchased packets attributable to this vendor; backend
 *               applies all lines under a single PR memo.
 *
 * Works for diamonds / color_stones / pearls — pass `material` prop.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
// Wave 3.49 — portal-wide carat/piece validation (Rule 1 + Rule 2).
import { validateCaratMovement } from '@/lib/caratValidation';
import { formatCarats, formatINR } from '@/lib/format';
import { Building2, ArrowDownToLine, Trash2, Plus, AlertCircle } from 'lucide-react';

const MATERIAL_META = {
  diamond:     { base: '/diamonds',     label: 'Diamond',      testidScope: 'diamond' },
  color_stone: { base: '/color-stones', label: 'Colour Stone', testidScope: 'color-stone' },
  pearl:       { base: '/pearls',       label: 'Pearl',        testidScope: 'pearl' },
};

export default function PurchaseReturnDialog({
  open,
  onOpenChange,
  // mode: 'packet' (Packet Detail) or 'vendor' (Vendor Detail)
  mode,
  material,
  companyId,
  // Required for mode='packet'
  packet,
  // Wave 3.62 — vendors the packet has received purchases from (in
  // chronological order). Length 0 means Purchase Return is not applicable;
  // length 1 shows a locked banner (unchanged UX); length > 1 shows a
  // selector so the operator picks the target vendor for the return.
  availableVendors = [],
  // Required for mode='vendor'
  vendorId,
  vendorName,
  onReturned,
}) {
  const meta = MATERIAL_META[material];
  const scope = meta?.testidScope || 'pkt';

  // Packet-mode local state
  const [pktCarats, setPktCarats] = useState('');
  const [pktPieces, setPktPieces] = useState('');
  const [pktCustomRate, setPktCustomRate] = useState('');
  const [pktNote, setPktNote] = useState('');
  const [pktReference, setPktReference] = useState('');
  // Wave 3.62 — selected vendor id (packet mode, when multi-vendor).
  const [pktVendorId, setPktVendorId] = useState('');

  // Vendor-mode local state
  const [eligible, setEligible] = useState([]);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [vendorLines, setVendorLines] = useState([]); // [{ packet_id, carats, pieces, rate_per_ct, note }]
  const [vendorNote, setVendorNote] = useState('');
  const [vendorReference, setVendorReference] = useState('');

  const [submitting, setSubmitting] = useState(false);

  // -- Reset when (re-)opened
  useEffect(() => {
    if (!open) return;
    if (mode === 'packet') {
      setPktCarats('');
      setPktPieces('');
      setPktCustomRate('');
      setPktNote('');
      setPktReference('');
      // Wave 3.62 — Default to the single available vendor when there is
      // only one; otherwise let the operator choose from the selector.
      if ((availableVendors || []).length === 1) {
        setPktVendorId(availableVendors[0].vendor_id);
      } else {
        setPktVendorId('');
      }
    } else {
      setVendorLines([]);
      setVendorNote('');
      setVendorReference('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, availableVendors]);

  // -- Load eligible packets in vendor mode
  useEffect(() => {
    if (!open || mode !== 'vendor' || !vendorId || !companyId || !meta) return;
    let cancelled = false;
    (async () => {
      setLoadingEligible(true);
      try {
        const { data } = await api.get(
          `${meta.base}/vendors/${vendorId}/eligible-purchase-return-packets`,
          { params: { company_id: companyId } },
        );
        if (!cancelled) setEligible(data || []);
      } catch (e) {
        if (!cancelled) toast.error(extractError(e, 'Failed to load eligible packets'));
      } finally {
        if (!cancelled) setLoadingEligible(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, vendorId, companyId, meta]);

  // ---- Computed derived values (packet mode) ----
  const pktCaratsNum = Number(pktCarats) || 0;
  const pktPiecesNum = Number(pktPieces) || 0;
  const pktRateNum = Number(pktCustomRate) > 0
    ? Number(pktCustomRate)
    : Number(packet?.current_rate_per_ct || 0);
  const pktExpectedValue = pktCaratsNum * pktRateNum;

  const pktExceedsBalance = packet
    && (pktCaratsNum - Number(packet.balance_carats || 0) > 1e-9
        || pktPiecesNum > Number(packet.balance_pieces || 0));

  // ---- Computed derived values (vendor mode) ----
  const vendorTotalCt = vendorLines.reduce((s, ln) => s + (Number(ln.carats) || 0), 0);
  const vendorTotalPcs = vendorLines.reduce((s, ln) => s + (Number(ln.pieces) || 0), 0);
  const vendorTotalValue = vendorLines.reduce((s, ln) => {
    const pkt = eligible.find((p) => p.id === ln.packet_id);
    const rate = Number(ln.rate_per_ct) > 0 ? Number(ln.rate_per_ct) : Number(pkt?.current_rate_per_ct || 0);
    return s + (Number(ln.carats) || 0) * rate;
  }, 0);

  // Per-packet aggregate guard: if the same packet appears on multiple lines,
  // the SUM of requested quantity must still fit within its balance.
  const aggByPacket = useMemo(() => {
    const map = new Map();
    for (const ln of vendorLines) {
      if (!ln.packet_id) continue;
      const cur = map.get(ln.packet_id) || { carats: 0, pieces: 0 };
      cur.carats += Number(ln.carats) || 0;
      cur.pieces += Number(ln.pieces) || 0;
      map.set(ln.packet_id, cur);
    }
    return map;
  }, [vendorLines]);

  // Compute any-line-exceeds-balance for submit gating.
  const vendorAnyExceeds = useMemo(() => {
    for (const [pid, agg] of aggByPacket.entries()) {
      const pkt = eligible.find((p) => p.id === pid);
      if (!pkt) continue;
      if (agg.carats - Number(pkt.balance_carats || 0) > 1e-9) return true;
      if (agg.pieces > Number(pkt.balance_pieces || 0)) return true;
    }
    // Also block if any individual line has 0/0 (no qty entered)
    for (const ln of vendorLines) {
      if (!ln.packet_id) continue;
      const ct = Number(ln.carats) || 0;
      const pcs = Number(ln.pieces) || 0;
      if (ct <= 0 && pcs <= 0) return true;
      if (ct < 0 || pcs < 0) return true;
    }
    return false;
  }, [aggByPacket, eligible, vendorLines]);

  // Unselected eligible packets (cannot select same packet twice)
  const availableEligible = useMemo(() => {
    const used = new Set(vendorLines.map((ln) => ln.packet_id));
    return eligible.filter((p) => !used.has(p.id));
  }, [eligible, vendorLines]);

  const addVendorLine = () => {
    setVendorLines((prev) => [...prev, { packet_id: '', carats: '', pieces: '', rate_per_ct: '', note: '' }]);
  };
  const removeVendorLine = (idx) => {
    setVendorLines((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateVendorLine = (idx, patch) => {
    setVendorLines((prev) => prev.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)));
  };

  // ---- Submit ----
  const submitPacketMode = async () => {
    if (!packet) return;
    // Wave 3.62 — When the packet has multiple qualifying vendors, the
    // operator MUST pick one; when there is exactly one it was auto-set
    // by the reset effect. Bail out early with a clear message otherwise.
    if ((availableVendors || []).length > 1 && !pktVendorId) {
      toast.error('Select the vendor to return this stock to.');
      return;
    }
    if ((availableVendors || []).length === 0) {
      toast.error('This packet has no vendored purchase — Purchase Return is not applicable.');
      return;
    }
    // Wave 3.49 Rule 1 + Rule 2.
    const ruleErr = validateCaratMovement({
      txnCarats: pktCaratsNum, txnPieces: pktPiecesNum,
      availableCarats: packet.balance_carats || 0,
      availablePieces: packet.balance_pieces || 0,
      packetNo: packet.packet_no,
      context: 'purchase return',
    });
    if (ruleErr) { toast.error(ruleErr); return; }
    setSubmitting(true);
    try {
      const payload = {
        carats: pktCaratsNum,
        pieces: pktPiecesNum,
        note: pktNote || '',
        reference: pktReference || '',
      };
      if (Number(pktCustomRate) > 0) {
        payload.rate_per_ct = Number(pktCustomRate);
      }
      // Wave 3.62 — pass the explicit vendor when known so the backend
      // routes the payable-decrement to the correct vendor even for
      // multi-vendor packets.
      const effectiveVendorId = pktVendorId || (availableVendors[0] && availableVendors[0].vendor_id);
      if (effectiveVendorId) payload.vendor_id = effectiveVendorId;
      const { data } = await api.post(`${meta.base}/packets/${packet.id}/purchase-return`, payload);
      toast.success(`Purchase return ${data.memo_no} created`);
      onOpenChange(false);
      if (onReturned) onReturned(data);
    } catch (e) {
      toast.error(extractError(e, 'Purchase return failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitVendorMode = async () => {
    if (!vendorId) return;
    const cleaned = vendorLines.filter((ln) => ln.packet_id);
    if (cleaned.length === 0) {
      toast.error('Add at least one packet line');
      return;
    }
    // Per-line validation
    for (const ln of cleaned) {
      const pkt = eligible.find((p) => p.id === ln.packet_id);
      if (!pkt) {
        toast.error('Selected packet not found in eligible list');
        return;
      }
      const ct = Number(ln.carats) || 0;
      const pcs = Number(ln.pieces) || 0;
      // Wave 3.49 Rule 1 + Rule 2 — both qty > 0; balance check; source
      // state must remain consistent post-return.
      const ruleErr = validateCaratMovement({
        txnCarats: ct, txnPieces: pcs,
        availableCarats: pkt.balance_carats || 0,
        availablePieces: pkt.balance_pieces || 0,
        packetNo: pkt.packet_no,
        context: 'vendor-mode purchase return',
      });
      if (ruleErr) { toast.error(ruleErr); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        vendor_id: vendorId,
        lines: cleaned.map((ln) => {
          const o = {
            packet_id: ln.packet_id,
            carats: Number(ln.carats) || 0,
            pieces: Number(ln.pieces) || 0,
            note: ln.note || '',
          };
          if (Number(ln.rate_per_ct) > 0) o.rate_per_ct = Number(ln.rate_per_ct);
          return o;
        }),
        note: vendorNote || '',
        reference: vendorReference || '',
      };
      const { data } = await api.post(`${meta.base}/packets/purchase-return-batch`, payload, {
        params: { company_id: companyId },
      });
      toast.success(`Purchase return ${data.memo_no} created (${data.lines?.length || 0} packet${(data.lines?.length || 0) === 1 ? '' : 's'})`);
      onOpenChange(false);
      if (onReturned) onReturned(data);
    } catch (e) {
      toast.error(extractError(e, 'Purchase return failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!meta) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[88vh] overflow-y-auto"
        data-testid={`purchase-return-dialog-${scope}`}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownToLine size={16} className="text-amber-700" />
            Purchase Return — {meta.label}
          </DialogTitle>
          <DialogDescription>
            {mode === 'packet'
              ? 'Return a portion of this purchased packet back to its original vendor. A PR memo will be generated and the vendor\'s payable will reduce.'
              : 'Return one or more purchased packets back to this vendor. A single PR memo will cover all selected lines.'}
          </DialogDescription>
        </DialogHeader>

        {/* Vendor banner — Wave 3.62: selector when the packet was purchased
             from multiple vendors, locked banner otherwise (unchanged UX). */}
        <Card className="p-3 bg-amber-50/40 border-amber-200">
          <div className="flex items-center gap-2 text-amber-900 text-sm">
            <Building2 size={14} />
            <span className="font-medium">Vendor:</span>
            {mode === 'packet' ? (
              (availableVendors || []).length > 1 ? (
                <div className="flex items-center gap-2 flex-1">
                  <Select
                    value={pktVendorId}
                    onValueChange={setPktVendorId}
                  >
                    <SelectTrigger
                      className="h-8 bg-white text-slate-800 border-slate-200 max-w-xs"
                      data-testid="purchase-return-vendor-select"
                    >
                      <SelectValue placeholder="Select vendor to return to…" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableVendors.map((v) => (
                        <SelectItem
                          key={v.vendor_id}
                          value={v.vendor_id}
                          data-testid={`purchase-return-vendor-option-${v.vendor_id}`}
                        >
                          <div className="flex flex-col">
                            <span className="text-sm">{v.vendor_name || v.vendor_id}</span>
                            <span className="text-[10px] text-slate-500">
                              received {formatCarats(v.carats_received)} ct · {v.pieces_received} pcs
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-[11px] text-amber-700">(multi-vendor packet)</span>
                </div>
              ) : (
                <>
                  <Badge variant="outline" className="bg-white text-slate-800 border-slate-200" data-testid="purchase-return-vendor">
                    {availableVendors?.[0]?.vendor_name || packet?.vendor_name || '—'}
                  </Badge>
                  <span className="text-[11px] text-amber-700 ml-1">(locked)</span>
                </>
              )
            ) : (
              <>
                <Badge variant="outline" className="bg-white text-slate-800 border-slate-200" data-testid="purchase-return-vendor">
                  {vendorName || vendorId || '—'}
                </Badge>
                <span className="text-[11px] text-amber-700 ml-1">(locked)</span>
              </>
            )}
          </div>
        </Card>

        {mode === 'packet' && packet && (
          <div className="space-y-4 mt-1">
            <Card className="p-3 bg-white">
              <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                <span>Packet</span>
                <span className="font-mono text-[12px] text-slate-700" data-testid="purchase-return-packet-no">{packet.packet_no}</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Available</div>
                  <div className="font-mono tabular-nums text-sm" data-testid="purchase-return-available-carats">
                    {formatCarats(packet.balance_carats || 0)} ct · {packet.balance_pieces || 0} pcs
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Rate (₹/ct)</div>
                  <div className="font-mono tabular-nums text-sm">
                    {Number(packet.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-400">Mode</div>
                  <div className="text-sm">{packet.valuation_mode === 'fixed' ? 'Fixed' : 'Running Avg'}</div>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs" htmlFor="pr-carats">Carats to return *</Label>
                <Input
                  id="pr-carats"
                  type="number"
                  step="0.01"
                  min="0"
                  value={pktCarats}
                  onChange={(e) => setPktCarats(e.target.value)}
                  placeholder="0.00"
                  data-testid="purchase-return-carats-input"
                />
              </div>
              <div>
                <Label className="text-xs" htmlFor="pr-pieces">Pieces to return *</Label>
                <Input
                  id="pr-pieces"
                  type="number"
                  min="0"
                  value={pktPieces}
                  onChange={(e) => setPktPieces(e.target.value)}
                  placeholder="0"
                  data-testid="purchase-return-pieces-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs" htmlFor="pr-rate">Custom rate (₹/ct) — optional</Label>
              <Input
                id="pr-rate"
                type="number"
                step="0.01"
                min="0"
                value={pktCustomRate}
                onChange={(e) => setPktCustomRate(e.target.value)}
                placeholder={`Defaults to packet rate ${Number(packet.current_rate_per_ct || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`}
                data-testid="purchase-return-rate-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">Leave blank to use packet&apos;s current rate.</div>
            </div>

            <div>
              <Label className="text-xs" htmlFor="pr-reference">Reference — optional</Label>
              <Input
                id="pr-reference"
                value={pktReference}
                onChange={(e) => setPktReference(e.target.value)}
                placeholder="e.g. debit note ref"
                data-testid="purchase-return-reference-input"
              />
            </div>

            <div>
              <Label className="text-xs" htmlFor="pr-note">Note — optional</Label>
              <Textarea
                id="pr-note"
                rows={2}
                value={pktNote}
                onChange={(e) => setPktNote(e.target.value)}
                placeholder="Reason for return"
                data-testid="purchase-return-note-input"
              />
            </div>

            <Card className="p-3 bg-slate-50 border-slate-200">
              <div className="flex items-center justify-between text-sm">
                <div className="text-slate-600">Return value (estimated)</div>
                <div className="font-mono tabular-nums text-slate-900" data-testid="purchase-return-estimated-value">
                  {formatINR(pktExpectedValue)}
                </div>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                Vendor&apos;s payable will reduce by this amount. Packet rate stays the same.
              </div>
            </Card>

            {pktExceedsBalance && (
              <div
                className="flex items-start gap-2 text-rose-700 text-[12px] bg-rose-50 border border-rose-200 rounded px-2 py-1.5"
                data-testid="purchase-return-exceeds-warning"
              >
                <AlertCircle size={14} className="mt-0.5 shrink-0" />
                <div>
                  Carats or pieces exceed packet&apos;s available balance
                  {pktCaratsNum - Number(packet.balance_carats || 0) > 1e-9 && <> ({pktCaratsNum} ct &gt; {packet.balance_carats} ct)</>}
                  {pktPiecesNum > Number(packet.balance_pieces || 0) && <> ({pktPiecesNum} pcs &gt; {packet.balance_pieces} pcs)</>}
                  .
                </div>
              </div>
            )}
          </div>
        )}

        {mode === 'vendor' && (
          <div className="space-y-3 mt-1">
            {loadingEligible ? (
              <div className="text-slate-500 text-sm py-6 text-center">Loading eligible packets…</div>
            ) : eligible.length === 0 ? (
              <Card className="p-6 text-center text-sm text-slate-500" data-testid="purchase-return-no-eligible">
                No purchased packets with balance found for this vendor.
              </Card>
            ) : (
              <>
                {vendorLines.length === 0 && (
                  <div className="text-xs text-slate-500 mt-1">Add packets to return. Only packets purchased from this vendor with remaining balance appear in the list.</div>
                )}
                {vendorLines.map((ln, idx) => {
                  const pkt = eligible.find((p) => p.id === ln.packet_id);
                  const ctNum = Number(ln.carats) || 0;
                  const pcsNum = Number(ln.pieces) || 0;
                  const rateNum = Number(ln.rate_per_ct) > 0 ? Number(ln.rate_per_ct) : Number(pkt?.current_rate_per_ct || 0);
                  const valNum = ctNum * rateNum;
                  // Aggregate qty for this packet across all lines (catches
                  // duplicate selections).
                  const agg = pkt ? (aggByPacket.get(pkt.id) || { carats: 0, pieces: 0 }) : { carats: 0, pieces: 0 };
                  const aggExceedsCt = pkt && (agg.carats - Number(pkt.balance_carats || 0) > 1e-9);
                  const aggExceedsPcs = pkt && (agg.pieces > Number(pkt.balance_pieces || 0));
                  const lineExceedsCt = pkt && (ctNum - Number(pkt.balance_carats || 0) > 1e-9);
                  const lineExceedsPcs = pkt && (pcsNum > Number(pkt.balance_pieces || 0));
                  const exceeds = lineExceedsCt || lineExceedsPcs || aggExceedsCt || aggExceedsPcs;
                  return (
                    <Card key={idx} className={`p-3 ${exceeds ? 'border-rose-300 bg-rose-50/30' : ''}`} data-testid={`purchase-return-line-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-slate-500">Line {idx + 1}</div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-rose-700 hover:bg-rose-50"
                          onClick={() => removeVendorLine(idx)}
                          data-testid={`purchase-return-line-remove-${idx}`}
                        >
                          <Trash2 size={12} className="mr-1" /> Remove
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-[11px]">Packet</Label>
                          {(() => {
                            const packetItems = [
                              ...(pkt ? [pkt] : []),
                              ...availableEligible,
                            ];
                            return (
                              <SearchableSelect
                                items={packetItems}
                                value={ln.packet_id || ''}
                                onValueChange={(v) => updateVendorLine(idx, { packet_id: v || '' })}
                                itemToLabel={(p) => `${p.packet_no} — ${formatCarats(p.balance_carats)} ct · ${p.balance_pieces} pcs · ₹${Number(p.current_rate_per_ct).toLocaleString('en-IN', { maximumFractionDigits: 0 })}/ct`}
                                itemToSubLabel={(p) => p.classification_shape || p.classification_schema || null}
                                searchKeys={['packet_no', 'classification_shape', 'classification_schema', 'description']}
                                placeholder={packetItems.length === 0 ? 'No eligible packets' : 'Choose packet…'}
                                searchPlaceholder="Search packet no / shape…"
                                disabled={packetItems.length === 0}
                                testId={`purchase-return-line-packet-${idx}`}
                              />
                            );
                          })()}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label className="text-[11px]">Carats *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={ln.carats}
                              onChange={(e) => updateVendorLine(idx, { carats: e.target.value })}
                              className={lineExceedsCt || aggExceedsCt ? 'border-rose-400 focus-visible:ring-rose-400' : ''}
                              data-testid={`purchase-return-line-carats-${idx}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[11px]">Pieces *</Label>
                            <Input
                              type="number"
                              min="0"
                              value={ln.pieces}
                              onChange={(e) => updateVendorLine(idx, { pieces: e.target.value })}
                              className={lineExceedsPcs || aggExceedsPcs ? 'border-rose-400 focus-visible:ring-rose-400' : ''}
                              data-testid={`purchase-return-line-pieces-${idx}`}
                            />
                          </div>
                          <div>
                            <Label className="text-[11px]">Rate (optional)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={ln.rate_per_ct}
                              onChange={(e) => updateVendorLine(idx, { rate_per_ct: e.target.value })}
                              placeholder={pkt ? Number(pkt.current_rate_per_ct).toString() : ''}
                              data-testid={`purchase-return-line-rate-${idx}`}
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-[11px] mt-1">
                          <div className="text-slate-500">
                            Available: {pkt ? `${formatCarats(pkt.balance_carats)} ct · ${pkt.balance_pieces} pcs` : '—'}
                          </div>
                          <div className="font-mono tabular-nums text-slate-700">
                            Value: {formatINR(valNum)}
                          </div>
                        </div>
                        {exceeds && (
                          <div
                            className="flex items-start gap-2 text-rose-700 text-[12px] bg-rose-50 border border-rose-200 rounded px-2 py-1.5 mt-1"
                            data-testid={`purchase-return-line-exceeds-${idx}`}
                          >
                            <AlertCircle size={14} className="mt-0.5 shrink-0" />
                            <div>
                              {(lineExceedsCt || lineExceedsPcs) && (
                                <div>
                                  Exceeds packet&apos;s available balance
                                  {lineExceedsCt && <> ({ctNum} ct &gt; {pkt?.balance_carats} ct)</>}
                                  {lineExceedsPcs && <> ({pcsNum} pcs &gt; {pkt?.balance_pieces} pcs)</>}
                                  .
                                </div>
                              )}
                              {!lineExceedsCt && !lineExceedsPcs && (aggExceedsCt || aggExceedsPcs) && (
                                <div>
                                  Combined with other lines for this packet, total exceeds available balance
                                  {aggExceedsCt && <> ({agg.carats} ct &gt; {pkt?.balance_carats} ct)</>}
                                  {aggExceedsPcs && <> ({agg.pieces} pcs &gt; {pkt?.balance_pieces} pcs)</>}
                                  .
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}

                <Button
                  variant="outline"
                  size="sm"
                  className="border-dashed"
                  onClick={addVendorLine}
                  disabled={availableEligible.length === 0 && vendorLines.length >= eligible.length}
                  data-testid="purchase-return-line-add"
                >
                  <Plus size={12} className="mr-1" /> Add packet line
                </Button>

                {vendorLines.length > 0 && (
                  <Card className="p-3 bg-slate-50 border-slate-200 mt-2" data-testid="purchase-return-vendor-totals">
                    <div className="text-[11px] uppercase text-slate-500 mb-2">Totals</div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <div className="text-[10px] uppercase text-slate-400">Carats</div>
                        <div className="font-mono tabular-nums">{formatCarats(vendorTotalCt)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400">Pieces</div>
                        <div className="font-mono tabular-nums">{vendorTotalPcs}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-slate-400">Value</div>
                        <div className="font-mono tabular-nums">{formatINR(vendorTotalValue)}</div>
                      </div>
                    </div>
                  </Card>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <div>
                    <Label className="text-xs" htmlFor="pr-vendor-reference">Reference — optional</Label>
                    <Input
                      id="pr-vendor-reference"
                      value={vendorReference}
                      onChange={(e) => setVendorReference(e.target.value)}
                      placeholder="e.g. debit note ref"
                      data-testid="purchase-return-vendor-reference-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs" htmlFor="pr-vendor-note">Note — optional</Label>
                    <Input
                      id="pr-vendor-note"
                      value={vendorNote}
                      onChange={(e) => setVendorNote(e.target.value)}
                      placeholder="Reason for return"
                      data-testid="purchase-return-vendor-note-input"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter className="pt-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="purchase-return-cancel"
          >
            Cancel
          </Button>
          <Button
            className="luxury-button"
            onClick={mode === 'packet' ? submitPacketMode : submitVendorMode}
            disabled={
              submitting
              || (mode === 'packet' && (
                (pktCaratsNum <= 0 && pktPiecesNum <= 0)
                || pktExceedsBalance
                // Wave 3.62 — require an explicit vendor pick when the
                // packet has multiple qualifying vendors.
                || ((availableVendors || []).length > 1 && !pktVendorId)
                || ((availableVendors || []).length === 0)
              ))
              || (mode === 'vendor' && (
                vendorLines.filter((ln) => ln.packet_id).length === 0
                || vendorAnyExceeds
              ))
            }
            data-testid="purchase-return-submit"
          >
            {submitting ? 'Posting…' : 'Confirm Return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
